import { Component, Injector, OnInit, forwardRef, inject, input, model, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
import { DxNumberBoxComponent, DxTextBoxComponent, DxTextBoxModule } from 'devextreme-angular';
import { ValueChangedEvent } from 'devextreme/ui/text_box';
import { CalculateValidationTextFn, ControlSupportingTextComponent } from '../../internal/control-supporting-text/control-supporting-text.component';
import { LayoutControlComponent } from '../../internal/layout-control/layout-control.component';
import { ControlLabelComponent } from '../../internal/control-label/control-label.component';

@Component({
  selector: 'app-textbox',
  templateUrl: './textbox.component.html',
  styleUrls: ['./textbox.component.scss'],
  imports: [LayoutControlComponent, ControlLabelComponent, DxTextBoxModule, ControlSupportingTextComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TextboxComponent),
      multi: true
    }
  ],
})
export class TextboxComponent implements OnInit, ControlValueAccessor {
  private dx = viewChild.required<DxTextBoxComponent | DxNumberBoxComponent>('dx');

  private injector = inject(Injector);
  ngControl: NgControl = null;

  caption = input.required<string>();
  value = model<string>('');
  calculateValidationText = input<CalculateValidationTextFn | string>();
  helpText = input<string>();
  readonly = input<boolean>(false);
  disabled = model<boolean>(false);

  ngOnInit(): void {
    try {
      this.ngControl = this.injector.get(NgControl);
    }
    catch {
      // No NgControl
    }
  }

  get control(): FormControl {
    return this.ngControl?.control as FormControl;
  }

  get isValid(): boolean {
    return !this.control || (this.control.invalid !== true || (this.control.pristine && this.control.untouched));
  }

  onValueChanged(e: ValueChangedEvent): void {
    if (e.value !== this.value()) {
      this.onChange(e.value);
      this.value.set(e.value as string);
    }
  }

  /*
   * ControlValueAccessor interface methods
   */

  writeValue(obj: string): void {
    this.value.set(obj);
  }

  private onChange: (value: unknown) => void = () => { };
  registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = fn;
  }

  onTouched: () => void = () => { };
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled.set(!!isDisabled);
  }
}
